import React, { FC } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import {
  AuthorisedUser,
  Collateral,
  CollateralIdentifierType,
  CollateralPrice,
  CollateralType,
  EntityIdAndType,
  EntityState,
  EntityType,
  EntityTypeEnum,
  Product,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  WorkflowActions
} from 'api'

import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { createProductGetterSelector } from 'selectors/productSelectors'
import { NotificationActions, NotificationType, ShowDialogArgs } from 'store/notification'
import { createWorkflowReferenceDataAvailableEventsSelector, createWorkflowReferenceDataEntitySelector } from 'selectors/workflowSelectors'

import { CollateralDialogComponent, ReadonlyFields } from './CollateralDialogComponent'
import { Locale, Translation } from 'locale'
import { isCollateralMort } from 'model'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { isNotEmpty } from 'utils/predicate'
import { getUniqueWarningMessagesFromValidationIssues } from './collateralDialogUtils'
import { useSelector } from 'react-redux'

export type CollateralDialogEditProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  workflowId: string
  collateralEntityId: string
  collateralPriceEntityId: string
  onClose?: () => unknown
}

interface StateProps {
  collateralEntity: ReferenceDataEntityType<Collateral> | null
  collateralPriceEntity: ReferenceDataEntityType<CollateralPrice> | null
  availableEvents: ReferenceDataManagementEvent[]
  getCollateral: (collateralId: string) => Collateral | null
  getProduct: (productSectorId: string) => Product | null
  authorisedUser: AuthorisedUser
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: Collateral | CollateralPrice; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: CollateralDialogEditProps) => ownProps.workflowId
const collateralEntityIdSelector = (_state: {}, ownProps: CollateralDialogEditProps) => ownProps.collateralEntityId
const collateralPriceEntityIdSelector = (_state: {}, ownProps: CollateralDialogEditProps) => ownProps.collateralPriceEntityId

const mapStateToProps = createStructuredSelector({
  collateralEntity: createWorkflowReferenceDataEntitySelector(EntityType.COLLATERAL, workflowIdSelector, collateralEntityIdSelector),
  collateralPriceEntity: createWorkflowReferenceDataEntitySelector(
    EntityType.COLLATERAL_PRICE,
    workflowIdSelector,
    collateralPriceEntityIdSelector
  ),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector),
  getCollateral: createEntityGetterSelector(EntityType.COLLATERAL),
  getProduct: createProductGetterSelector,
  authorisedUser: createAuthorisedUserSelector()
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow,
  onShowDialog: NotificationActions.showDialog
}

const CollateralDialogEditContainer: FC<CollateralDialogEditProps> = props => {
  const {
    collateralEntityId,
    getCollateral,
    getProduct,
    onUpdate,
    workflowId,
    collateralEntity,
    collateralPriceEntity,
    availableEvents,
    onSubmitWorkflow,
    onCancelWorkflow,
    onClose,
    authorisedUser,
    showDialog
  } = props
  const getProductFromProductSectorId = useSelector(createProductGetterSelector)

  if (!collateralEntity || !collateralPriceEntity) {
    return null
  }

  const originalCollateral = getCollateral(collateralEntityId)
  const collateral = collateralEntity.data
  const collateralPrice = collateralPriceEntity.data

  const collateralValidationIssues = collateralEntity.validationIssues
  const collateralPriceValidationIssues = collateralPriceEntity.validationIssues
  const validationIssues = (collateralValidationIssues ?? []).concat(collateralPriceValidationIssues ?? [])

  const onUpdateCollateral = (partialCollateral: Partial<Collateral>) => {
    onUpdate({
      entityId: collateralEntity.id,
      entityType: EntityTypeEnum.COLLATERAL,
      updatedEntity: { ...collateral, ...partialCollateral },
      workflowId
    })
  }

  const onUpdateCollateralPrice = (cleanPrice: number, collateralId: string = collateral.id) => {
    //deal with undefined
    onUpdate({
      entityId: collateralPriceEntity.id,
      entityType: EntityTypeEnum.COLLATERAL_PRICE,
      updatedEntity: {
        ...collateralPrice,
        collateralId,
        cleanPrice
      },
      workflowId
    })
  }

  const handleSave = async () => {
    const originalProduct = originalCollateral && getProductFromProductSectorId(originalCollateral.productSectorId)
    const newProduct = getProductFromProductSectorId(collateral.productSectorId)

    const warningMessages = getUniqueWarningMessagesFromValidationIssues(validationIssues)
    const micHasChanged = originalProduct && newProduct ? originalProduct.micCode !== newProduct.micCode : false

    if (isNotEmpty(warningMessages) || micHasChanged) {
      const warnings = warningMessages.join('\n')
      const micChangeMessage = micHasChanged ? Locale.translate(Translation.SC_COLLATERAL_REPORTING_WARNING_MESSAGE) : ''
      const result = await showDialog({
        type: NotificationType.Confirm,
        title: Translation.TC_COLLATERAL_SAVE_CONFIRMATION,
        messageText: `${warnings} \n ${micChangeMessage} \n \n Are you sure you want to continue?`,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        defaultButton: Translation.TC_BUTTON_YES,
        cancelButton: Translation.TC_BUTTON_NO
      })

      if (result === Translation.TC_BUTTON_YES) {
        onSubmitWorkflow(workflowId)
        if (onClose) {
          onClose()
        }
      }
    } else {
      onSubmitWorkflow(workflowId)
      if (onClose) {
        onClose()
      }
    }
  }

  const handleClose = () => {
    onCancelWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  const title = collateralEntity.entityState === EntityState.NEW ? Locale.translate(Translation.TC_ADD_NEW_COLLATERAL) : collateral.name

  const cannotUpdate = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const isStandardCollateral = collateral.collateralType === CollateralType.STANDARD
  const product = getProduct(collateral.productSectorId)

  const readOnly: ReadonlyFields = {
    status: cannotUpdate,
    collateralIdentifierType: cannotUpdate || collateralEntity.entityState === EntityState.EXISTING,
    name: cannotUpdate,
    isin:
      cannotUpdate ||
      (collateralEntity.entityState === EntityState.EXISTING && collateral.collateralIdentifierType === CollateralIdentifierType.ISIN),
    fisin: cannotUpdate,
    cusip: collateralEntity.entityState === EntityState.EXISTING && collateral.collateralIdentifierType === CollateralIdentifierType.CUSIP,
    cfi: cannotUpdate,
    currency: cannotUpdate,
    countryCode: cannotUpdate,
    productSectorId: cannotUpdate,
    product: true,
    tradingType: cannotUpdate,
    cleanPrice: cannotUpdate,
    minimumTicketSize: cannotUpdate,
    ticketIncrement: cannotUpdate,
    issuer: cannotUpdate,
    issuerLei: cannotUpdate,
    issueDate: cannotUpdate,
    couponAccruedInterestDate: cannotUpdate,
    couponFirstDate: cannotUpdate,
    couponMaturityDate: cannotUpdate,
    firstTradeInstant: true,
    couponType: cannotUpdate,
    basisConvention: cannotUpdate,
    coupon: cannotUpdate,
    couponFrequency: cannotUpdate,
    totalIssuedNominalAmount: cannotUpdate,
    collateralType: cannotUpdate,
    indexId: cannotUpdate || isStandardCollateral,
    indexSpread: cannotUpdate,
    indexLag: cannotUpdate || isStandardCollateral,
    baseIndexValue: cannotUpdate || isStandardCollateral,
    prospectus: true,
    seniority: cannotUpdate,
    save: !availableEvents.includes(ReferenceDataManagementEvent.SUBMIT),
    ric: cannotUpdate,
    lastUpdatedAtByUserOrSystem: true,
    lastUpdatedByUser: true,
    assetType: cannotUpdate,
    onRecognisedExchange: cannotUpdate,
    inEsmaFirds: true,
    eeaVenueEligible: true,
    ukVenueEligible: true,
    complianceOverride: cannotUpdate,
    issue: cannotUpdate,

    //CollateralAdditionalDetailsPanel Fields
    assetStatus: true,
    assetStatusEffectiveDate: true,
    bondType: true,
    callableFlag: true,
    countryOfIncorporation: true,
    countryOfIssuance: true,
    countryOfRiskAll: true,
    countryOfRiskPrimary: true,
    countryOfRiskUltimateParentAll: true,
    countryOfRiskUltimateParentPrimary: true,
    couponRateEffectiveDate: true,
    denominationIncrement: true,
    domicile: true,
    ecbIndicator: true,
    exDividendCalendar: true,
    exDividendRule: true,
    exDividendValue: true,
    factor: cannotUpdate || !isCollateralMort(collateral),
    factorDate: cannotUpdate || !isCollateralMort(collateral),
    mbsPriceDate: true,
    fitchRating: true,
    floatIndexType: true,
    floatMultiplier: true,
    floatOffset: true,
    floaterFlag: true,
    floatingRateFixingDate: true,
    id: true,
    industrySectorCode: true,
    industrySubSectorCode: true,
    isSecuredFlag: true,
    issuerOrgId: true,
    lookBack: true,
    mifidIndexIdentifier: true,
    mifidIndexName: true,
    mifidIndexTerm: true,
    minimumDenomination: true,
    moodysRating: true,
    multiStepFloatFlag: true,
    offeringType: true,
    perpetualSecurityFlag: true,
    privatePlacementFlag: true,
    refinitivClassificationScheme: true,
    refinitivAssetType: true,
    refinitivBaseIndex: true,
    assetSubType: true,
    debtType: true,
    regulationIndicator: true,
    rule144aIndicator: true,
    sandPRating: true,
    settlementDateConvention: true,
    sinkableFlag: true,
    totalAmountOutstanding: true,
    micReportDate: true,
    series: true
  }

  return (
    <CollateralDialogComponent
      title={title}
      readOnly={readOnly}
      originalCollateral={originalCollateral}
      collateral={collateralEntity.data}
      collateralPrice={collateralPriceEntity.data}
      collateralPriceValidationIssues={collateralPriceEntity.validationIssues ?? []}
      validationIssues={validationIssues}
      product={product}
      onClose={handleClose}
      onSave={handleSave}
      onUpdateCollateral={onUpdateCollateral}
      onUpdateCollateralPrice={onUpdateCollateralPrice}
      userPreference={authorisedUser.userPreference}
    />
  )
}

export const CollateralDialogEdit = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(CollateralDialogEditContainer)
)
